/**
 * Local-only media storage.
 *
 * Files written here NEVER leave the device — they're tracked in the local
 * SQLite store via the `local_media` table and persisted to a sandboxed
 * filesystem chosen per platform:
 *
 * - **Tauri (desktop / mobile):** `$APPLOCALDATA/media/<uuid>.<ext>` via
 *   `@tauri-apps/plugin-fs`.
 * - **Web (modern browsers):** Origin Private File System (OPFS), under
 *   `media/<uuid>.<ext>`. Sandbox per origin, persisted by the browser.
 *
 * Both platforms reuse the same `local_media` schema — only the meaning
 * of `local_path` changes ("relative to AppLocalData" vs. "relative to
 * OPFS root"). All higher-level callers (route, mutations, lightbox)
 * use the same async API regardless of platform.
 */
import type { LocalDb, LocalDbRow } from "@coach/sync";

const MEDIA_DIR = "media";

export function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

/** True if the running browser exposes OPFS (`navigator.storage.getDirectory`). */
export function isOpfsAvailable(): boolean {
  return (
    typeof navigator !== "undefined" &&
    typeof navigator.storage !== "undefined" &&
    typeof navigator.storage.getDirectory === "function"
  );
}

/**
 * True if local-only media storage is usable on this platform. Used by
 * the route to decide whether to surface the cloud-vs-local upload picker
 * and the move-between-tiers actions.
 */
export function isLocalMediaAvailable(): boolean {
  return isTauri() || isOpfsAvailable();
}

/* ─── storage-persistence (web only) ─────────────────────────────── */

/** True if the browser has already granted persistent storage for this origin. */
export async function isStoragePersisted(): Promise<boolean> {
  if (isTauri()) return true;
  if (
    typeof navigator === "undefined" ||
    typeof navigator.storage === "undefined" ||
    typeof navigator.storage.persisted !== "function"
  ) {
    return false;
  }
  try {
    return await navigator.storage.persisted();
  } catch {
    return false;
  }
}

/**
 * Ask the browser to mark this origin as persistent so that OPFS files
 * aren't evicted under storage pressure. Chrome decides silently;
 * Firefox shows a small permission prompt; Safari does it implicitly.
 * Returns the resulting "persisted" state.
 */
export async function requestStoragePersist(): Promise<boolean> {
  if (isTauri()) return true;
  if (
    typeof navigator === "undefined" ||
    typeof navigator.storage === "undefined" ||
    typeof navigator.storage.persist !== "function"
  ) {
    return false;
  }
  try {
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}

/* ─── filesystem abstraction ────────────────────────────────────── */

interface MediaFs {
  writeFile(path: string, bytes: Uint8Array): Promise<void>;
  readFile(path: string): Promise<Uint8Array>;
  remove(path: string): Promise<void>;
}

let tauriFsPromise: Promise<typeof import("@tauri-apps/plugin-fs")> | null = null;
function loadTauriFs() {
  if (!tauriFsPromise) tauriFsPromise = import("@tauri-apps/plugin-fs");
  return tauriFsPromise;
}

const tauriFs: MediaFs = {
  async writeFile(path, bytes) {
    const fs = await loadTauriFs();
    const { BaseDirectory } = fs;
    if (!(await fs.exists(MEDIA_DIR, { baseDir: BaseDirectory.AppLocalData }))) {
      await fs.mkdir(MEDIA_DIR, {
        baseDir: BaseDirectory.AppLocalData,
        recursive: true,
      });
    }
    await fs.writeFile(path, bytes, { baseDir: BaseDirectory.AppLocalData });
  },
  async readFile(path) {
    const fs = await loadTauriFs();
    const bytes = await fs.readFile(path, {
      baseDir: fs.BaseDirectory.AppLocalData,
    });
    const out = new Uint8Array(bytes.byteLength);
    out.set(bytes);
    return out;
  },
  async remove(path) {
    const fs = await loadTauriFs();
    try {
      await fs.remove(path, { baseDir: fs.BaseDirectory.AppLocalData });
    } catch (e) {
      console.warn("[local-media] tauri remove failed", path, e);
    }
  },
};

/** Walk OPFS path segments, optionally creating intermediate dirs. */
async function opfsDirFor(segments: string[], create: boolean): Promise<FileSystemDirectoryHandle> {
  let dir = await navigator.storage.getDirectory();
  for (const seg of segments) {
    if (!seg) continue;
    dir = await dir.getDirectoryHandle(seg, { create });
  }
  return dir;
}

const opfsFs: MediaFs = {
  async writeFile(path, bytes) {
    const parts = path.split("/");
    const filename = parts.pop()!;
    const dir = await opfsDirFor(parts, true);
    const handle = await dir.getFileHandle(filename, { create: true });
    const writable = await handle.createWritable();
    try {
      const buf = new Uint8Array(bytes.byteLength);
      buf.set(bytes);
      await writable.write(buf);
    } finally {
      await writable.close();
    }
  },
  async readFile(path) {
    const parts = path.split("/");
    const filename = parts.pop()!;
    const dir = await opfsDirFor(parts, false);
    const handle = await dir.getFileHandle(filename, { create: false });
    const file = await handle.getFile();
    const buf = await file.arrayBuffer();
    return new Uint8Array(buf);
  },
  async remove(path) {
    const parts = path.split("/");
    const filename = parts.pop()!;
    try {
      const dir = await opfsDirFor(parts, false);
      await dir.removeEntry(filename);
    } catch (e) {
      console.warn("[local-media] opfs remove failed", path, e);
    }
  },
};

function fs(): MediaFs {
  if (isTauri()) return tauriFs;
  if (isOpfsAvailable()) return opfsFs;
  throw new Error("Lokaler Speicher ist auf dieser Plattform nicht verfügbar.");
}

/* ─── public model + queries ────────────────────────────────────── */

export interface LocalMediaItem {
  id: string;
  orgId: string;
  createdAt: string;
  filename: string;
  mime: string;
  bytes: number;
  /**
   * Path inside the platform sandbox. On Tauri this is relative to
   * `$APPLOCALDATA`; on web it's relative to the OPFS root. Always a
   * forward-slash path like `media/<uuid>.png`.
   */
  localPath: string;
}

interface LocalMediaRow extends LocalDbRow {
  id: string;
  org_id: string;
  created_at: string;
  filename: string;
  mime: string;
  bytes: number;
  local_path: string;
}

function rowToItem(row: LocalMediaRow): LocalMediaItem {
  return {
    id: row.id,
    orgId: row.org_id,
    createdAt: row.created_at,
    filename: row.filename,
    mime: row.mime,
    bytes: row.bytes,
    localPath: row.local_path,
  };
}

export async function listLocalMedia(db: LocalDb, orgId: string): Promise<LocalMediaItem[]> {
  const rows = await db.query<LocalMediaRow>(
    "SELECT * FROM local_media WHERE org_id = ? ORDER BY created_at DESC",
    [orgId],
  );
  return rows.map(rowToItem);
}

/* ─── write / read / delete ─────────────────────────────────────── */

/**
 * Persist a `File`/`Blob` to the local media directory and register a
 * `local_media` row. Returns the new item. Works on both Tauri and web
 * (OPFS); throws only if the running platform has neither.
 */
export async function saveLocalMedia(
  db: LocalDb,
  orgId: string,
  file: File,
): Promise<LocalMediaItem> {
  const id = crypto.randomUUID();
  const ext = extensionFor(file.name, file.type);
  const localPath = `${MEDIA_DIR}/${id}${ext}`;

  const bytes = new Uint8Array(await file.arrayBuffer());
  await fs().writeFile(localPath, bytes);

  const createdAt = new Date().toISOString();
  const mime = file.type || "application/octet-stream";
  await db.exec(
    `INSERT INTO local_media (id, org_id, created_at, filename, mime, bytes, local_path)
     VALUES (?, ?, ?, ?, ?, ?, ?)`,
    [id, orgId, createdAt, file.name, mime, file.size, localPath],
  );

  return {
    id,
    orgId,
    createdAt,
    filename: file.name,
    mime,
    bytes: file.size,
    localPath,
  };
}

/**
 * Write pre-existing bytes (e.g. downloaded from the cloud) into the
 * local store and return the new item. Used by the cloud→local move.
 */
export async function importLocalMediaFromBytes(
  db: LocalDb,
  orgId: string,
  args: { filename: string; mime: string; bytes: Uint8Array },
): Promise<LocalMediaItem> {
  const id = crypto.randomUUID();
  const ext = extensionFor(args.filename, args.mime);
  const localPath = `${MEDIA_DIR}/${id}${ext}`;
  await fs().writeFile(localPath, args.bytes);

  const createdAt = new Date().toISOString();
  const mime = args.mime || "application/octet-stream";
  await db.exec(
    `INSERT INTO local_media (id, org_id, created_at, filename, mime, bytes, local_path)
     VALUES (?, ?, ?, ?, ?, ?, ?)`,
    [id, orgId, createdAt, args.filename, mime, args.bytes.byteLength, localPath],
  );

  return {
    id,
    orgId,
    createdAt,
    filename: args.filename,
    mime,
    bytes: args.bytes.byteLength,
    localPath,
  };
}

export async function deleteLocalMedia(db: LocalDb, item: LocalMediaItem): Promise<void> {
  try {
    await fs().remove(item.localPath);
  } catch (e) {
    console.warn("[local-media] remove skipped", item.localPath, e);
  }
  await db.exec("DELETE FROM local_media WHERE id = ?", [item.id]);
}

/**
 * Read a local-only file into a Blob URL suitable for `<img src>` /
 * `<video src>`. Caller is responsible for revoking the URL when done.
 */
export async function localMediaToBlobUrl(item: LocalMediaItem): Promise<string> {
  const bytes = await fs().readFile(item.localPath);
  const buf = new Uint8Array(bytes).buffer;
  const blob = new Blob([buf], { type: item.mime });
  return URL.createObjectURL(blob);
}

/** Read raw bytes for a local item (used to push to the cloud). */
export async function readLocalMediaBytes(item: LocalMediaItem): Promise<Uint8Array> {
  return fs().readFile(item.localPath);
}

function extensionFor(filename: string, mime: string): string {
  const fromName = /\.[A-Za-z0-9]{1,5}$/.exec(filename)?.[0];
  if (fromName) return fromName.toLowerCase();
  const byMime: Record<string, string> = {
    "image/png": ".png",
    "image/jpeg": ".jpg",
    "image/webp": ".webp",
    "image/gif": ".gif",
    "image/avif": ".avif",
    "video/mp4": ".mp4",
    "video/webm": ".webm",
    "video/quicktime": ".mov",
    "application/pdf": ".pdf",
  };
  return byMime[mime] ?? "";
}
